import { allowNumberOnly, formatMobileNumber, toMobileDigits } from 'helpers/CommonHelper';
import { formattedMobileNumberLength } from 'helpers/ValidationHelper';
import React from 'react';
import { Form } from 'react-bootstrap';
import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';

interface MobileNumberInputProps<TFieldValues extends FieldValues> {
  // generic rather than Control<any>: a form's Control carries its own field names, and
  // react-hook-form's validate option makes that invariant - Control<any> is rejected
  control: Control<TFieldValues, any, any>;
  name: FieldPath<TFieldValues>;
  /** Render a bare <input> instead of react-bootstrap's Form.Control. */
  plain?: boolean;
  [key: string]: any;
}

/**
 * Phone field that reads 231-313-9966 while keeping the bare 2313139966 in form state,
 * so validation rules, API payloads and values loaded from the API all stay digits and
 * only the display carries the dashes.
 */
const MobileNumberInput = <TFieldValues extends FieldValues>({
  control,
  name,
  plain = false,
  ...rest
}: MobileNumberInputProps<TFieldValues>) => {
  const { field } = useController({ control, name });

  const inputProps = {
    ...rest,
    type: 'text',
    name: field.name,
    ref: field.ref,
    // the dashes are part of what the user sees, so the cap has to cover them
    maxLength: formattedMobileNumberLength,
    value: formatMobileNumber(field.value),
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      field.onChange(toMobileDigits(event.target.value)),
    onBlur: field.onBlur,
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => allowNumberOnly(event),
  };

  return plain ? <input {...inputProps} /> : <Form.Control {...inputProps} />;
};

export default MobileNumberInput;
